import { useEffect, useState } from "react";

import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { RTU } from "../../types/RTU";
import { TreeTable } from "primereact/treetable";
import { useNavigate } from "react-router-dom";
import { parseLocationAddress } from "./dashboardLocationNormalization";
import { useTranslation } from "react-i18next";

interface PopupProps {
  rtus: RTU[];
  onClose: () => void;
}

interface TreeNode {
  key: string;
  data: any;
  children?: TreeNode[];
}

const PopupComponent: React.FC<PopupProps> = ({ rtus, onClose }) => {
  const [groupedRTUs, setGroupedRTUs] = useState<TreeNode[]>([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Group RTUs by city (since all are in same country based on pie chart click)
  const groupByCity = (data: RTU[]): TreeNode[] => {
    const grouped: { [city: string]: RTU[] } = {};

    data.forEach((rtu) => {
      const { city } = parseLocationAddress(rtu.address);
      if (!grouped[city]) grouped[city] = [];
      grouped[city].push(rtu);
    });

    return Object.keys(grouped).map((city) => ({
      key: city,
      data: { location: city, isCity: true },
      children: grouped[city].map((rtu) => ({
        key: String(rtu.id),
        data: { ...rtu, isCity: false },
      })),
    }));
  };

  useEffect(() => {
    const nodes = groupByCity(rtus);
    setGroupedRTUs(nodes);
  }, [rtus]);

  const openRtuDetail = (event: any) => {
    const node = event.node;
    if (node && node.data && node.data.id) {
      const rtuId = node.data.id;
      const rtu = rtus.find((r) => r.id === rtuId);
      if (rtu) {
        navigate(`/rtupage/${rtuId}`, { state: { rtu } });
      }
    }
  };

  // Get country from first RTU for title
  const { country } = parseLocationAddress(rtus[0]?.address);

  // City column body template
  const cityBodyTemplate = (rowData: any) => {
    if (rowData.data.isCity) {
      return (
        <span style={{ fontWeight: 600 }}>
          {rowData.data.location}
        </span>
      );
    }
    return null;
  };

  return (
      <Dialog
        header={`RTUs location: ${country}`}
        visible
        modal
        position="center"
        className="dashboard-status-detail-dialog"
        style={{ width: "min(64rem, calc(100vw - 7rem))", maxWidth: "96vw" }}
        contentStyle={{ padding: "0.75rem 1rem 1rem" }}
        headerStyle={{ padding: "0.75rem 1rem", borderBottom: "1px solid #e9ecef" }}
        onHide={onClose}
        blockScroll
      >
        <TreeTable
          value={groupedRTUs}
          scrollable
          scrollHeight="360px"
          onRowClick={openRtuDetail}
          rowClassName={(node) => ({ "dashboard-status-detail-dialog__row": Boolean(node.data?.id) })}
          showGridlines
          stripedRows
          className="location-popup-treetable"
          tableStyle={{ width: "100%", tableLayout: "fixed" }}
        >
          <Column
            field="location"
            header="City"
            body={cityBodyTemplate}
            style={{ width: "25%" }}
            expander
          />
          <Column
            field="id"
            header={t("dashboard.tables.columns.wellId", { defaultValue: "Well ID" })}
            style={{ width: "7rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            field="name"
            header={t("dashboard.tables.columns.rtuId", { defaultValue: "RTU ID" })}
            style={{ width: "28%" }}
          />
          <Column
            field="provider"
            header="Producer"
            style={{ width: "22%" }}
          />
          <Column
            field="locationSource"
            header="Location Source"
            body={(node) => node.data?.locationSource || "Unknown"}
            style={{ width: "10rem" }}
          />
        </TreeTable>
      </Dialog>
  );
};

export default PopupComponent;
